/** Route slug ↔ `/legal/immigration-travel-law`. */
export const IMMIGRATION_TRAVEL_LAW_SLUG = "immigration-travel-law";

export const immigrationTravelLawDefaultData = {
  heroImage:
    "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?auto=format&fit=crop&w=1500&q=80",
  galleryImages: [
    "https://images.unsplash.com/photo-1556388158-158e896b103d?auto=format&fit=crop&w=900&q=80",
    "https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?auto=format&fit=crop&w=900&q=80",
    "https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?auto=format&fit=crop&w=900&q=80",
  ],
  stats: [
    { value: "GIS", label: "Ghana Immigration" },
    { value: "Act 573", label: "Core statute" },
    { value: "24h", label: "Expert triage" },
  ],
  funnelTagline:
    "Visas → lawful stay/work → GIS compliance → consult early, not at the gate",
  focusAreas: ["Visa applications", "Work permits", "Residency issues", "Travel restrictions"],
  primaryCtaHeading: "Speak to an immigration lawyer",
  introduction: {
    headline: "WHY IMMIGRATION & TRAVEL LAW MATTERS IN GHANA",
    affectsLead: "Immigration and travel issues affect both:",
    affects: [
      "Ghanaians travelling, relocating, studying, or working abroad",
      "Foreign nationals entering, living, or working in Ghana",
    ],
    regulatoryLead: "In Ghana, immigration is regulated mainly by:",
    statutes: [
      { name: "Immigration Act, 2000 (Act 573)", detail: "Core law on entry, residence, and control." },
      { name: "Immigration Regulations, 2001 (L.I. 1691)", detail: "Practical rules on visas and permits." },
      { name: "Immigration Service Act, 2016 (Act 908)", detail: "Institutional framework for Ghana Immigration Service." },
    ],
    gisLead:
      "The Ghana Immigration Service (GIS) is the main authority regulating entry, exit, residence, and employment of foreign nationals.",
    mistakesLead: "Mistakes here can cause:",
    consequences: [
      "Visa refusals",
      "Denied boarding",
      "Entry refusal at the border",
      "Overstays",
      "Work-permit violations",
      "Fines or deportation",
      "Long-term travel restrictions",
    ],
  },
  miniSiteGoals: {
    title: "WHAT THIS HUB SHOULD HELP YOU ANSWER",
    subtitle: "High-intent questions from people ready to travel, relocate, hire, or regularize status:",
    questions: [
      "Do I need a visa to enter Ghana?",
      "Can I work in Ghana on a visitor visa?",
      "How does a work permit work?",
      "What is the difference between a residence permit and a work permit?",
      "What should I do if my visa is expiring?",
      "What documents do I need before travelling?",
      "What travel restrictions or entry rules should I check?",
      "What options do I have if my immigration application is delayed or refused?",
    ],
  },
  framework: {
    title: "GHANA’S IMMIGRATION LEGAL FRAMEWORK",
    mainHeading: "Main laws and institutions",
    mainItems: [
      { title: "Act 573", body: "Core law on entry, residence, and immigration control." },
      { title: "L.I. 1691", body: "Practical visa and permit procedures." },
      { title: "Act 908", body: "Regulates GIS institutionally." },
      {
        title: "Ghana Immigration Service",
        body: "Entry, exit, border control, residence, foreign employment compliance.",
      },
    ],
    employmentHeading: "Employment-linked immigration",
    employmentBody:
      "Where foreign nationals work in Ghana, rules interact with Labour Act, 2003 (Act 651) and investment routes through GIPC/GFZB as applicable.",
  },
  visaApplications: {
    title: "VISA APPLICATIONS: WHAT PEOPLE NEED TO KNOW",
    basicRuleHeading: "Basic rule",
    basicRuleLead: "A non-citizen entering Ghana must generally hold:",
    basicNeeds: ["A valid passport or travel document", "A valid visa where required"],
    exemptionsHeading: "Who may be exempt",
    exemptionsBody:
      "Citizens of ECOWAS states and nationals of countries with reciprocal arrangements may be exempt from Ghana entry-visa rules—always confirm current GIS and mission guidance.",
    issuesHeading: "Common visa pitfalls",
    issues: [
      "Not knowing if you qualify for visa-free entry",
      "Choosing the wrong visa category",
      "Assuming a “business visit” authorizes employment",
      "Incomplete supporting documents",
      "Booking travel before approval clears",
      "Misreading emergency entry or visa-on-arrival",
    ],
    visitorWarningHeading: "Very important",
    visitorWarningBody:
      "Visitors are not permitted to take employment or carry on business in Ghana unless specifically authorized—a leading source of violations.",
  },
  visaOnArrival: {
    title: "VISA ON ARRIVAL / EMERGENCY ENTRY VISA",
    intro:
      "Many travellers assume they can obtain a visa at the airport casually. GIS states Visa on Arrival / Emergency Entry Visa may be granted on prescribed conditions, and current GIS guidance emphasizes prior approval.",
    bullets: [
      "It is not a casual backup.",
      "Usually tied to urgent or exceptional situations.",
      "Do not assume automatic approval before boarding.",
      "Hosts or applicants typically must initiate GIS steps ahead of arrival.",
    ],
    alertBox:
      "Do not travel assuming you will get a visa at the airport without pre-approval. Confirm the latest GIS/mission requirements before ticketing.",
  },
  entryRequirements: {
    title: "ENTRY REQUIREMENTS & TRAVEL DOCUMENTS",
    checklistHeading: "Check before you fly",
    checklist: [
      "Valid passport",
      "Visa if required",
      "Invitation or purpose/supporting paperwork",
      "Return or onward travel evidence where needed",
      "Accommodation/host details",
      "Yellow fever certificate where applicable",
    ],
    yellowFeverHeading: "Yellow fever requirement",
    yellowFeverBody:
      "Official Ghana mission guidance requires travellers older than nine months entering or transiting Ghana to carry valid yellow fever vaccination evidence.",
    disruptionsHeading: "Disruptions often come from:",
    disruptions: [
      "Passport validity shortfalls",
      "Missing vaccination records",
      "Inconsistent details across paperwork",
      "Wrong declared purpose",
      "Missing sponsor/invitation proof",
    ],
  },
  workPermits: {
    title: "WORK PERMIT PROCESSES IN GHANA",
    realityLead: "Foreign nationals who wish to work and reside normally need GIS authorization aligning with Ghanaian labour and investment rules.",
    distinctionsHeading: "Three different permissions",
    distinctions: [
      { label: "Visa", detail: "Permission to seek lawful entry." },
      { label: "Residence permit", detail: "Permission to remain in Ghana according to granted terms." },
      { label: "Work approval", detail: "Permission to undertake employment commercially—coordinate with GIS routes." },
    ],
    employersBody:
      "GIS indicates lawfully admitted foreign nationals may apply via the Comptroller-General using company dossiers—often including incorporation evidence, sponsorship letters, employer attestations, and GIPC-aligned materials where required.",
    issuesHeading: "Employer pitfalls",
    issues: [
      "Staff begin work before status is finalized",
      "Misreading sponsorship or quota norms",
      "Incomplete filings or mismatched registrations",
      "Assuming visitor status can be corrected later",
    ],
    simpleGuidance:
      "Neither employee nor employer should assume paperwork can always be cured after the fact—it creates deportation risk, sanctions, and business disruption.",
  },
  residenceIssues: {
    title: "RESIDENCE PERMIT ISSUES",
    appliesHeading: "Common profiles",
    applies: ["Expatriates", "Dependants", "Students", "Long-term residents", "Families linked to Ghanaian nationals", "Investors", "NGOs & shareholders"],
    categoriesHeading: "GIS categories commonly referenced",
    categories: [
      "Student residence permits",
      "Dependant permits",
      "Visa/permit extensions",
      "Coordinated work-and-residence tracks",
      "Indefinite residence",
      "Right of abode",
    ],
    problemsHeading: "Typical hurdles",
    problems: [
      "Permit expiry lapses",
      "Delayed renewal queues",
      "Dependant versus worker mix-ups",
      "Children aging out",
      "Switching visitor status to lawful work/residency",
      "Uncertainty on longer-term lawful stay",
    ],
  },
  indefiniteAbode: {
    title: "INDEFINITE RESIDENCE & RIGHT OF ABODE",
    indefiniteLead: "Indefinite residence (per GIS summaries)",
    indefiniteBullets: [
      "May remain indefinitely in Ghana",
      "May enter without a visa subject to GIS conditions",
      "May work employed or self-employed without a standalone work permit (per granted terms)",
    ],
    abodeLead: "Right of abode highlights",
    abodeBullets: [
      "Potential routes for former Ghanaian citizens after acquiring foreign nationality—subject to proof",
      "Persons of African descent in Diaspora satisfying statutory tests",
    ],
    whyMatters:
      "These routes anchor long-run identity planning, relocation, succession, investments, and family unity—consult counsel before promising timelines.",
  },
  overstayProblems: {
    title: "RESIDENCY & OVERSTAY ISSUES",
    typical: [
      "Overstaying after visitor periods",
      "Working on the wrong endorsement",
      "Failing renewals before expiry",
      "Assuming marriage alone fixes status without GIS steps",
      "Travelling while renewals linger without verifying re-entry",
    ],
    coreMessage:
      "Immigration errors compound costs when postponed—act ahead of expiry, not after dashboards turn red.",
  },
  travelRestrictions: {
    title: "TRAVEL RESTRICTIONS & COMPLIANCE",
    meanings: [
      "Nationality-sensitive visa mandates",
      "Health documentation mandates",
      "Visitor-status activity limits",
      "Deportation or inadmissibility flags",
      "Pending GIS enforcement notices",
      "Airline document checks refusing boarding",
    ],
    refusalNote:
      "Official Ghana missions note visas or entries may be refused where deportation orders or similar bars apply.",
    tipHeading: "Pre-trip verification",
    tipSteps: ["Passport validity", "Correct visa/permit tier", "Vaccinations", "Matching declared purpose vs documents", "Prior refusals or bars cleared"],
  },
  commonProblems: {
    title: "COMMON REAL-LIFE ISSUES",
    ghanaiansHeading: "Ghanaians travelling abroad",
    ghanaians: [
      "Dubious “guaranteed visa” brokers",
      "Incomplete dossiers",
      "Ignoring embassy nuances",
      "Ticketing ahead of adjudication",
      "Fake proofs",
      "Hiding earlier refusals",
    ],
    foreignHeading: "Foreign nationals entering Ghana",
    foreign: ["Working pre-approval", "Wrong endorsement", "Out-of-date stickers", "Sponsor inconsistencies", "Backlogs on work/residence bundles", "Dependant misunderstandings"],
    employerHeading: "Employers/sponsors",
    employer: ["Hiring absent GIS sanction", "Assuming incorporation alone suffices", "Silo’d GIPC, labour & immigration files", "Missing renewal trackers"],
  },
  practicalTips: {
    title: "PRACTICAL TIPS",
    beforeApply: {
      heading: "Before any visa/permit filing",
      items: ["Lock exact GIS category", "Follow official checklist", "Align names/passports", "Avoid grey-market shortcuts", "Never falsify disclosures"],
    },
    beforeTravel: {
      heading: "Before departing",
      items: ["Check passport timelines", "Yellow fever proofs", "Visa exemptions vs mandates", "Single vs multi-entry nuances", "Host pre-approval done"],
    },
    planWork: {
      heading: "If intending to work in Ghana",
      items: ["No visitor loopholes for wage work", "Queue proper work/residence combos", "Keep employer & GIS correspondence", "Diarise expiry"],
    },
    facingIssue: {
      heading: "If already in jeopardy",
      items: ["Act immediately", "Collate chronological records", "Do not disregard refusal letters", "Lawyer-guided remediation"],
    },
  },
  redFlags: {
    title: "RED FLAGS TO WATCH OUT FOR",
    items: [
      "Guaranteed visa pledges",
      "“Fly now, papers later” advice",
      "Forged invitations",
      "Coached lies on visa forms",
      "Employers ordering work without approvals",
      "Ignored renewals",
      "Mismatching passports, names, or dates across evidence",
    ],
  },
  editorialPlaybook: {
    title: "HIGH-VALUE CONTENT CLUSTER",
    visaPages: ["How to Apply for a Ghana Visa the Right Way", "Visa on Arrival in Ghana: What It Really Means", "Common Reasons Visa Applications Stumble"],
    workPages: ["Can a Foreigner Work in Ghana on a Visitor Visa?", "Work Permit vs Residence Permit in Ghana", "Documents Employers Need for Expat Work Authorization"],
    residencePages: ["How to Renew a Residence Permit in Ghana", "Dependant Permits Explained", "Indefinite Residence vs Right of Abode"],
    travelPages: ["Travel Checklist for Entering Ghana", "Yellow Fever, Passport Rules & Border Compliance", "What Causes Entry Refusal or Delays"],
    articlesHeading: "Featured article themes",
    videosHeading: "Video themes",
    videos: ["GIS categories demystified", "Work bundles for employers", "Border readiness checklist"],
    leadCtasHeading: "Suggested CTAs",
    leadCtas: [
      "Check my visa options",
      "Help with work permit processing",
      "Speak to an immigration lawyer",
      "Resolve my residence permit",
      "Book immigration consultation",
      "Review my travel dossier",
      "Expatriate compliance desk",
    ],
  },
  funnelApplication: {
    title: "INFOBOXX FUNNEL APPLICATION",
    hooksHeading: "Hooks",
    hooks: ["Can I work in Ghana on a visitor visa?", "Do I need a visa to enter Ghana?", "My permit is expiring — what next?"],
    education: {
      label: "Education",
      text: "Visa/permit distinctions, GIS categories, sponsor duties, traveller documents.",
    },
    authority: { label: "Authority", text: "Act 573, L.I. 1691, Act 908, GIS directives, embassy advisories." },
    conversion: {
      label: "Conversion",
      text: "Consultations, dossier vetting, employer compliance, remediation strategy.",
    },
  },
  finalTakeaway: {
    title: "FINAL TAKEAWAY",
    pillars: ["Enter lawfully", "Stay lawfully", "Work lawfully", "Avoid long-tail bans"],
    mistakeLead: "The costliest slips happen when people:",
    mistakes: ["Pick wrong categories", "Travel before adjudication finishes", "Work without approvals", "Ignore expiry clocks"],
    safeMessage:
      "The safest immigration decision is the informed decision made early—not the frantic fix at Kotoka.",
  },
  bottomLine: {
    title: "BOTTOM LINE",
    lead: "Immigration isn’t merely crossing borders—it is sustaining lawful status everywhere you land.",
    smooth: "Planned filings · aligned employers · truthful paperwork",
    and: "vs",
    stressful: "Overstays · refusals · enforcement risk",
    closing: "Map GIS requirements before tickets and payroll commitments.",
  },
  faqs: [
    {
      q: "Can I enter Ghana as a visitor and start working later?",
      a: "Generally no—visitor classifications do not double as blanket employment authorization; specific GIS approval is normally required.",
    },
    {
      q: "What is the difference between a work permit and a residence permit?",
      a: "Work authorization concerns lawful remuneration activity; residence deals with lawful stay. Many assignees coordinate both under GIS-directed procedures.",
    },
    {
      q: "Can I rely on visa on arrival?",
      a: "Only within GIS-stated gateways, and prevailing guidance underscores pre-approved emergency or VoA filings—not walk-up certainty.",
    },
    {
      q: "Do I need yellow fever proof?",
      a: "Ghana missions state travellers nine months or older arriving or transiting need valid vaccination evidence.",
    },
    {
      q: "Can ECOWAS citizens skip Ghana visas?",
      a: "Often yes regarding standard ECOWAS free movement—but carry compliant ID/passport guidance and verify any parallel border checks.",
    },
    {
      q: "Do investors/shareholders obtain special routes?",
      a: "Yes—GIS publishes distinct investor or quota-linked categories that must align with GIPC/GFZB evidence where mandated.",
    },
  ],
  articles: [
    {
      id: "itl-a-1",
      slug: "immigration-ghana-visa-application-guide",
      title: "How to Apply for a Ghana Visa the Right Way",
      excerpt:
        "Categories vs reality, reciprocity exemptions, supporting letters, biometric timelines, and why visitor ≠ worker under GIS scrutiny.",
      type: "Guide",
      readTime: "10 min read",
      author: "InfoBoxx Editorial Desk",
      publishedAt: "Updated recently",
      updatedAt: "Updated recently",
      socialText: "How to Apply for a Ghana Visa the Right Way — InfoBoxx",
      coverImage: "https://images.unsplash.com/photo-1556388158-158e896b103d?auto=format&fit=crop&w=800&q=80",
      tags: ["Act 573", "GIS", "Visas"],
      pullQuote: "Book travel only after the correct GIS category clears.",
      takeaways: ["Match purpose to endorsed visa", "Pre-approve emergency entry", "Carry corroborating host proof"],
      video: {
        title: "GIS categories demystified",
        duration: "5:30",
        poster: "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?auto=format&fit=crop&w=800&q=80",
      },
      body: [
        {
          heading: "Document discipline",
          text: "",
          bullets: ["Passports with adequate blank pages & validity", "Sponsor dossiers cohesive with declared mission"],
        },
        {
          heading: "Emergency / VoA reality",
          text: "Travel only after affirmative GIS acknowledgement—never assume kiosk issuance.",
        },
      ],
    },
    {
      id: "itl-a-2",
      slug: "immigration-can-foreigner-work-visitor-visa-ghana",
      title: "Can a Foreigner Work in Ghana on a Visitor Visa?",
      excerpt:
        "Visitor bans on employment, interplay with Labour Act obligations, sequencing work-and-residence packets, fines, deportation exposures.",
      type: "Checklist",
      readTime: "8 min read",
      author: "Partner Knowledge Team",
      publishedAt: "This week",
      updatedAt: "This week",
      socialText: "Visitor visa vs lawful work in Ghana",
      coverImage: "https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?auto=format&fit=crop&w=800&q=80",
      tags: ["Act 651", "Employers", "Risk"],
      pullQuote: "Payroll activation needs GIS-aligned papers—not optimism.",
      takeaways: ["Freeze hiring until dossier accepted", "Map investor vs employee routes", "Document each renewal"],
      video: {
        title: "Work bundles for employers",
        duration: "6:05",
        poster: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=800&q=80",
      },
      body: [
        {
          heading: "Employer compliance",
          bullets: ["Incorporation evidence", "GIPC letters when triggered", "Manning quotas & expat plans"],
        },
      ],
    },
    {
      id: "itl-a-3",
      slug: "immigration-work-permit-vs-residence-ghana",
      title: "Work Permit vs Residence Permit in Ghana",
      excerpt:
        "Layering stay vs remunerated activity permits, indefinite stay vs contingent approvals, renewal loops, dependents, students, investors.",
      type: "Expert Explainer",
      readTime: "9 min read",
      author: "Consumer Guidance Unit",
      publishedAt: "Reviewed this month",
      updatedAt: "Reviewed this month",
      socialText: "Understand Ghana work vs residence permissions",
      coverImage: "https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?auto=format&fit=crop&w=800&q=80",
      tags: ["Permits", "Dependants", "GIS"],
      pullQuote: "Three permissions—visa, residence, work—solve different puzzles.",
      takeaways: ["Audit each family member’s class", "Track expirations", "Secure legal bridging if pending"],
      video: {
        title: "Border readiness checklist",
        duration: "4:50",
        poster: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=800&q=80",
      },
      body: [
        {
          heading: "Indefinite vs right of abode",
          text: "Each offers distinct freedoms—professional advice is mandatory before staking relocation plans.",
        },
      ],
    },
  ],
  videos: [
    {
      id: "itl-v-1",
      title: "GIS categories demystified",
      duration: "5:30",
      poster: "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?auto=format&fit=crop&w=800&q=80",
      description: "Match traveller intent to lawful endorsements.",
    },
    {
      id: "itl-v-2",
      title: "Work bundles for employers",
      duration: "6:05",
      poster: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=800&q=80",
      description: "Sponsor dossiers, GIPC touchpoints, expat timelines.",
    },
    {
      id: "itl-v-3",
      title: "Border readiness checklist",
      duration: "4:50",
      poster: "https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?auto=format&fit=crop&w=800&q=80",
      description: "Passports, vaccines, invitation coherence.",
    },
  ],
  podcasts: [
    {
      id: "itl-p-1",
      episodeLabel: "Episode 1",
      duration: "31 min",
      title: "Act 573 Crash Course for Busy Travellers",
      description: "From ECOWAS movement to restrictive nationalities.",
      host: "Afrimore Legal Desk",
      publishedAt: "Jun 2026",
      coverImage: "https://images.unsplash.com/photo-1556388158-158e896b103d?auto=format&fit=crop&w=800&q=80",
    },
    {
      id: "itl-p-2",
      episodeLabel: "Episode 2",
      duration: "29 min",
      title: "HR + GIS: Coordinating Talent Mobility",
      description: "What legal and people teams must sync monthly.",
      host: "Consumer Guidance Unit · Infoboxx",
      publishedAt: "May 2026",
      coverImage: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=800&q=80",
    },
    {
      id: "itl-p-3",
      episodeLabel: "Mini",
      duration: "18 min",
      title: "Yellow Fever & Airline Screening Stories",
      description: "When health annexes—not visas—strand passengers.",
      host: "Afrimore Legal Desk",
      publishedAt: "Apr 2026",
      coverImage: "https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?auto=format&fit=crop&w=800&q=80",
    },
  ],
};
